import { apiGet, apiPost, apiPut, apiDelete, apiUpload } from './client';

// ===== Types =====

export interface ExpenseReceipt {
  id: number;
  description: string | null;
  receiptDate: string | null;
  amount: number | null;
  reimburse: boolean;
  hasFile: boolean;
  fileName: string | null;
  contentType: string | null;
  fileSize: number | null;
  createdAt: string | null;
}

export interface ExpenseSheet {
  id: number;
  description: string | null;
  state: string;
  isReimbursed: boolean | null;
  userId: number | null;
  projectId: number | null;
  user: { id: number; email: string; firstName: string | null; lastName: string | null } | null;
  project: { id: number; name: string | null; fullName: string | null } | null;
  totalAmount: number;
  receiptCount: number;
  receipts: ExpenseReceipt[];
  createdAt: string | null;
  updatedAt: string | null;
}

export interface CreateExpenseSheetInput {
  projectId?: number;
  description?: string;
}

export interface UpdateExpenseSheetInput {
  description?: string;
  projectId?: number | null;
}

export interface CreateReceiptInput {
  description?: string;
  receiptDate?: string;
  amount?: number;
  reimburse?: boolean;
}

export interface UpdateReceiptInput {
  description?: string;
  receiptDate?: string;
  amount?: number;
  reimburse?: boolean;
}

// ===== Expense Sheet API =====

export async function getUserExpenseSheets(filters?: { state?: string; projectId?: number }): Promise<ExpenseSheet[]> {
  const params = new URLSearchParams();
  if (filters?.state) params.set('state', filters.state);
  if (filters?.projectId) params.set('projectId', String(filters.projectId));
  const query = params.toString();
  const res = await apiGet<{ data: ExpenseSheet[] }>(`/expenses${query ? `?${query}` : ''}`);
  return res.data;
}

export async function getExpenseSheet(id: number): Promise<ExpenseSheet> {
  const res = await apiGet<{ data: ExpenseSheet }>(`/expenses/${id}`);
  return res.data;
}

export async function createExpenseSheet(input: CreateExpenseSheetInput): Promise<ExpenseSheet> {
  const res = await apiPost<{ data: ExpenseSheet }>('/expenses', input);
  return res.data;
}

export async function updateExpenseSheet(id: number, input: UpdateExpenseSheetInput): Promise<ExpenseSheet> {
  const res = await apiPut<{ data: ExpenseSheet }>(`/expenses/${id}`, input);
  return res.data;
}

export async function deleteExpenseSheet(id: number): Promise<void> {
  await apiDelete<{ data: { success: boolean } }>(`/expenses/${id}`);
}

// ===== Expense Sheet Workflow =====

export async function submitExpenseSheet(id: number): Promise<ExpenseSheet> {
  const res = await apiPost<{ data: ExpenseSheet }>(`/expenses/${id}/submit`);
  return res.data;
}

export async function cancelExpenseSheet(id: number): Promise<ExpenseSheet> {
  const res = await apiPost<{ data: ExpenseSheet }>(`/expenses/${id}/cancel`);
  return res.data;
}

export async function reopenExpenseSheet(id: number): Promise<ExpenseSheet> {
  const res = await apiPost<{ data: ExpenseSheet }>(`/expenses/${id}/reopen`);
  return res.data;
}

export async function approveExpenseSheet(id: number): Promise<ExpenseSheet> {
  const res = await apiPost<{ data: ExpenseSheet }>(`/expenses/${id}/approve`);
  return res.data;
}

export async function rejectExpenseSheet(id: number, reason?: string): Promise<ExpenseSheet> {
  const res = await apiPost<{ data: ExpenseSheet }>(`/expenses/${id}/reject`, reason ? { reason } : undefined);
  return res.data;
}

export async function reimburseExpenseSheet(id: number): Promise<ExpenseSheet> {
  const res = await apiPost<{ data: ExpenseSheet }>(`/expenses/${id}/reimburse`);
  return res.data;
}

export async function bulkApproveExpenseSheets(ids: number[]): Promise<{ approved: number[]; failed: { id: number; error: string }[] }> {
  const res = await apiPost<{ data: { approved: number[]; failed: { id: number; error: string }[] } }>('/expenses/bulk/approve', { ids });
  return res.data;
}

export async function bulkRejectExpenseSheets(ids: number[], reason?: string): Promise<{ rejected: number[]; failed: { id: number; error: string }[] }> {
  const res = await apiPost<{ data: { rejected: number[]; failed: { id: number; error: string }[] } }>('/expenses/bulk/reject', { ids, reason });
  return res.data;
}

export async function bulkDeleteExpenseSheets(ids: number[]): Promise<{ deleted: number[]; failed: { id: number; error: string }[] }> {
  const res = await apiPost<{ data: { deleted: number[]; failed: { id: number; error: string }[] } }>('/expenses/bulk/delete', { ids });
  return res.data;
}

// ===== Admin =====

export async function getAllExpenseSheets(filters?: { state?: string; userId?: number; projectId?: number; adminView?: boolean }): Promise<ExpenseSheet[]> {
  const params = new URLSearchParams();
  if (filters?.state) params.set('state', filters.state);
  if (filters?.userId) params.set('userId', String(filters.userId));
  if (filters?.projectId) params.set('projectId', String(filters.projectId));
  if (filters?.adminView) params.set('adminView', 'true');
  const query = params.toString();
  const res = await apiGet<{ data: ExpenseSheet[] }>(`/expenses/admin/all${query ? `?${query}` : ''}`);
  return res.data;
}

export async function getPendingExpenseSheets(): Promise<ExpenseSheet[]> {
  const res = await apiGet<{ data: ExpenseSheet[] }>('/expenses/admin/pending');
  return res.data;
}

// ===== Receipt API =====

export async function getReceipts(expenseSheetId: number): Promise<ExpenseReceipt[]> {
  const res = await apiGet<{ data: ExpenseReceipt[] }>(`/expenses/${expenseSheetId}/receipts`);
  return res.data;
}

export async function createReceipt(expenseSheetId: number, input: CreateReceiptInput): Promise<ExpenseReceipt> {
  const res = await apiPost<{ data: ExpenseReceipt }>(`/expenses/${expenseSheetId}/receipts`, input);
  return res.data;
}

export async function updateReceipt(expenseSheetId: number, receiptId: number, input: UpdateReceiptInput): Promise<ExpenseReceipt> {
  const res = await apiPut<{ data: ExpenseReceipt }>(`/expenses/${expenseSheetId}/receipts/${receiptId}`, input);
  return res.data;
}

export async function deleteReceipt(expenseSheetId: number, receiptId: number): Promise<void> {
  await apiDelete<{ data: { success: boolean } }>(`/expenses/${expenseSheetId}/receipts/${receiptId}`);
}

export async function uploadReceiptFile(expenseSheetId: number, receiptId: number, file: File): Promise<ExpenseReceipt> {
  const res = await apiUpload<{ data: ExpenseReceipt }>(`/expenses/${expenseSheetId}/receipts/${receiptId}/upload`, file);
  return res.data;
}

export async function getReceiptDownloadUrl(expenseSheetId: number, receiptId: number): Promise<string> {
  const res = await apiGet<{ data: { downloadUrl: string } }>(`/expenses/${expenseSheetId}/receipts/${receiptId}/download`);
  return res.data.downloadUrl;
}
